import React from 'react';
import { FragmentData, gql } from '@ts-gql/tag';
import { useMutation } from '@ts-gql/apollo';

import { Toggle } from '@reckon-web/toggle';
import { useToasts } from '@reckon-web/toast';

import { usersFragment } from '../UsersTable';

// gql
const fragment = gql`
  fragment User_Active on TenantUser {
    __typename
    active
  }
` as import('../../../../__generated__/ts-gql/User_Active').type;

const UPDATE_ACTIVE = gql`
  mutation UpdateTenantUserStatus($input: TenantUserStatusInput!) {
    updateTenantUserStatus(input: $input)
  }
` as import('../../../../__generated__/ts-gql/UpdateTenantUserStatus').type;

// component
export function Active({
  tenantUser,
  rootCanDeactivate,
  currentRole,
  canEditUser,
}: {
  tenantUser: FragmentData<typeof usersFragment>;
  rootCanDeactivate: boolean;
  currentRole: string;
  canEditUser?: boolean;
}) {
  const { addToast } = useToasts();
  const [updateStatus] = useMutation(UPDATE_ACTIVE);

  const onToggle = async (value: boolean) => {
    if (currentRole === 'root' && !value && !rootCanDeactivate) {
      return addToast({
        tone: 'critical',
        title: 'Cannot deactivate user',
        message:
          'Before deacitvating this user please make sure there is a replacement active ROOT user to avoid not having access to user management',
      });
    }

    if (tenantUser.cloudId) {
      try {
        const data = await updateStatus({
          variables: {
            input: {
              cloudId: tenantUser.cloudId,
              modifiedBy: 'TUM',
              active: value,
            },
          },
          optimisticResponse: {
            __typename: 'Mutation',
            updateTenantUserStatus: value,
          },
          update: (cache) => {
            cache.writeFragment({
              id: tenantUser.cloudId || '',
              fragment: fragment,
              data: {
                __typename: 'TenantUser',
                active: value,
              },
            });
          },
        });
        addToast({
          tone: 'positive',
          title: 'User status update success',
        });
        if (!data.data?.updateTenantUserStatus) {
          addToast({
            tone: 'warning',
            title: "Something isn't right, please check the console",
          });
        }
      } catch (error: any) {
        let err = error?.message || error;
        if (err.includes('GraphQL error:'))
          err = err.replace('GraphQL error: ', '');
        addToast({
          tone: 'critical',
          title: `Update failed`,
          message: `${err}`,
        });
      }
    }
  };

  return (
    <Toggle
      checked={tenantUser.active || undefined}
      onChange={(value) => onToggle(value)}
      disabled={!canEditUser}
    />
  );
}
